import { EXPERIENCE_LEVELS, SEXES } from '@steado/admin-contract/user-detail';
import { DIET_TYPES, GOAL_TYPES } from '@steado/admin-contract/users';
import {
  WAIT_TIP_ICONS,
  WAIT_TIP_TEXT_MAX,
  type AdminWaitTip,
  type AdminWaitTipAudience,
  type WaitTipIconName,
} from '@steado/admin-contract/wait-tips';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useId, useState } from 'react';

import { Drawer } from '@/components/shell/Drawer';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';

import { WaitTipIcon } from './WaitTipIcon';
import {
  audienceSummary,
  EMPTY_WAIT_TIP_FORM,
  formIssues,
  formValuesOf,
  toCreateRequest,
  toUpdateRequest,
  type WaitTipFormValues,
} from './wait-tip-form';
import { createWaitTip, updateWaitTip, waitTipsQueryOptions } from './wait-tips-query';
import type { WaitTipsSearch } from './wait-tips-search';

export interface WaitTipsScreenProps {
  readonly search: WaitTipsSearch;
  readonly onSearchChange: (next: WaitTipsSearch) => void;
}

const ADMIN_ONLY = 'Editing wait tips requires the admin role.';

/**
 * Radix has no empty option — `value=""` clears the select rather than
 * selecting something — so "no filter" needs a value of its own.
 */
const ALL = '__all__';

const COLUMNS: readonly TableColumn[] = [
  { key: 'slug', label: 'Slug', width: '1.1fr' },
  { key: 'copy', label: 'Copy', width: '2.4fr' },
  { key: 'weight', label: 'Weight', width: '0.7fr', align: 'right' },
  { key: 'audience', label: 'Shown to', width: '1fr' },
  { key: 'state', label: '', width: '0.6fr', align: 'right' },
];

const ICON_LABELS: Record<WaitTipIconName, string> = {
  utensils: 'Utensils',
  camera: 'Camera',
  dumbbell: 'Dumbbell',
  messageCircle: 'Message',
  cloud: 'Cloud',
  droplet: 'Droplet',
  calendar: 'Calendar',
  trendingUp: 'Trending up',
  swap: 'Swap',
};

/** The four audience facets, each "empty means everyone". */
const AUDIENCE_FACETS = [
  { key: 'dietType', label: 'Diet', values: DIET_TYPES },
  { key: 'goal', label: 'Goal', values: GOAL_TYPES },
  { key: 'sex', label: 'Sex', values: SEXES },
  { key: 'experienceLevel', label: 'Training experience', values: EXPERIENCE_LEVELS },
] as const satisfies readonly {
  key: keyof AdminWaitTipAudience;
  label: string;
  values: readonly string[];
}[];

/**
 * The copy shown while a plan generates.
 *
 * The list leads and the editor is a drawer over it, because the question an
 * operator arrives with is "what are we showing people", not "let me write
 * one". Two things in that editor are load-bearing rather than decorative: the
 * character counter is against a real limit — 110 is three lines at the width
 * the app renders and a fourth pushes the layout — and the phone-width preview
 * is how an operator sees that before shipping it. The API enforces both, plus
 * the rule that a tip may not claim the plan is ready; the form says so first.
 */
export function WaitTipsScreen({ search, onSearchChange }: WaitTipsScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const filterId = useId();

  /**
   * What the drawer is doing: closed, composing a new tip, or editing the tip
   * with this id. One piece of state rather than an `isOpen` beside an id,
   * because "open with no tip and not new" is not a state that exists.
   */
  const [editing, setEditing] = useState<{ readonly id: string | null } | null>(null);
  const [values, setValues] = useState<WaitTipFormValues>(EMPTY_WAIT_TIP_FORM);

  const page = useQuery(waitTipsQueryOptions(api, search));

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'wait-tips'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing?.id == null
        ? createWaitTip(api, toCreateRequest(values))
        : updateWaitTip(api, editing.id, toUpdateRequest(values)),
    onSuccess: () => {
      setEditing(null);
      setValues(EMPTY_WAIT_TIP_FORM);
      invalidate();
    },
    retry: false,
  });

  /**
   * Retiring is the same write as any other edit, so it goes through the same
   * endpoint rather than a second one. There is no delete anywhere in this
   * feature: the app's cache version is the newest write in the locale, and a
   * delete could move that backwards.
   */
  const setActive = useMutation({
    mutationFn: ({ tip, isActive }: { tip: AdminWaitTip; isActive: boolean }) =>
      updateWaitTip(api, tip.id, { ...toUpdateRequest(formValuesOf(tip)), isActive }),
    onSuccess: invalidate,
    retry: false,
  });

  const openEditor = (tip: AdminWaitTip | null): void => {
    setEditing({ id: tip?.id ?? null });
    setValues(tip === null ? EMPTY_WAIT_TIP_FORM : formValuesOf(tip));
    save.reset();
  };

  const rows: readonly TableRow[] = (page.data?.items ?? []).map((tip) => ({
    id: tip.id,
    onOpen: () => {
      openEditor(tip);
    },
    cells: [
      <TextCell key="slug" value={tip.slug} sub={`icon: ${tip.iconKey}`} mono />,
      <div key="copy" className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[13px] text-pretty text-foreground">{tip.text}</span>
        <span
          className={cn(
            'font-mono text-xs',
            // The limit is a layout fact, not a style preference: past it the
            // copy wraps to a fourth line on a 320px phone.
            tip.text.length > WAIT_TIP_TEXT_MAX ? 'text-err-fg' : 'text-muted-foreground',
          )}
        >
          {tip.text.length} / {WAIT_TIP_TEXT_MAX}
        </span>
      </div>,
      <TextCell key="weight" value={String(tip.weight)} align="right" mono weight="medium" />,
      <TextCell key="audience" value={audienceSummary(tip.audience)} tone="muted" />,
      <div key="state" className="flex justify-end">
        <BadgeCell label={tip.isActive ? 'live' : 'retired'} tone={tip.isActive ? 'ok' : 'mute'} />
      </div>,
    ],
  }));

  const isNew = editing?.id == null;
  const issues = formIssues(values, isNew);
  const blocked = issues.length > 0 || !can.mutate;

  return (
    <div className="flex max-w-[1100px] flex-col gap-3">
      {page.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{page.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {setActive.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{setActive.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        caption="Wait tips"
        title="What the app shows while a plan builds"
        count={
          <span className="flex items-center gap-2">
            <Label htmlFor={filterId} className="sr-only">
              Showing
            </Label>
            <Select
              value={search.active ?? ALL}
              onValueChange={(next) => {
                onSearchChange({
                  ...search,
                  active: next === ALL ? undefined : (next as WaitTipsSearch['active']),
                  cursor: undefined,
                });
              }}
            >
              <SelectTrigger id={filterId} aria-label="Showing" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            {can.mutate ? (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  openEditor(null);
                }}
              >
                <PlusIcon aria-hidden="true" className="size-3.5" />
                New tip
              </Button>
            ) : null}
          </span>
        }
        columns={COLUMNS}
        rows={rows}
        isLoading={page.isPending}
        emptyTitle="No wait tips yet"
        emptyBody="The app falls back to its built-in list until there are some, so nothing is broken while this is empty."
        {...(can.mutate
          ? {
              emptyAction: (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    openEditor(null);
                  }}
                >
                  <PlusIcon aria-hidden="true" className="size-3.5" />
                  New tip
                </Button>
              ),
            }
          : {})}
        footer={
          page.data?.pagination.hasMore === true ? (
            <div className="flex justify-end border-t border-border px-[18px] py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onSearchChange({ ...search, cursor: page.data?.pagination.nextCursor ?? undefined });
                }}
              >
                Older tips
              </Button>
            </div>
          ) : undefined
        }
      />

      {editing === null ? null : (
        <WaitTipEditor
          isNew={isNew}
          values={values}
          canMutate={can.mutate}
          issues={issues}
          isSaving={save.isPending}
          saveError={save.isError ? save.error.message : null}
          isRetireBusy={setActive.isPending}
          onChange={setValues}
          onSave={() => {
            if (!blocked) save.mutate();
          }}
          onClose={() => {
            setEditing(null);
            setValues(EMPTY_WAIT_TIP_FORM);
            save.reset();
          }}
          {...(isNew
            ? {}
            : {
                onSetActive: (isActive: boolean) => {
                  const tip = page.data?.items.find((candidate) => candidate.id === editing.id);
                  if (tip !== undefined) setActive.mutate({ tip, isActive });
                },
              })}
        />
      )}
    </div>
  );
}

interface WaitTipEditorProps {
  readonly isNew: boolean;
  readonly values: WaitTipFormValues;
  readonly canMutate: boolean;
  readonly issues: readonly string[];
  readonly isSaving: boolean;
  readonly saveError: string | null;
  readonly isRetireBusy: boolean;
  readonly onChange: (next: WaitTipFormValues) => void;
  readonly onSave: () => void;
  readonly onClose: () => void;
  readonly onSetActive?: (isActive: boolean) => void;
}

/** The composer, over the list rather than above it. */
function WaitTipEditor({
  isNew,
  values,
  canMutate,
  issues,
  isSaving,
  saveError,
  isRetireBusy,
  onChange,
  onSave,
  onClose,
  onSetActive,
}: WaitTipEditorProps) {
  const slugId = useId();
  const localeId = useId();
  const iconId = useId();
  const textId = useId();
  const weightId = useId();
  const activeId = useId();

  return (
    <Drawer
      title={isNew ? 'New wait tip' : `Editing ${values.slug}`}
      subtitle="Shown while a plan generates. The app caches these and falls back to its built-in list if this endpoint is unreachable, so nothing here can hold up a plan."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={issues.length > 0 || !canMutate || isSaving}
            title={canMutate ? (issues.length === 0 ? undefined : issues.join(' ')) : ADMIN_ONLY}
          >
            {isNew ? 'Add tip' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {saveError === null ? null : (
          <Alert role="alert" tone="err">
            <AlertDescription>{saveError}</AlertDescription>
          </Alert>
        )}

        {issues.length === 0 ? null : (
          <p className="rounded-md border border-border bg-muted px-4 py-3 text-[13px] text-muted-foreground">
            Before this can be saved: {issues.join(' ')}
          </p>
        )}

        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor={slugId} className="text-xs font-medium text-muted-foreground">
              Slug
            </Label>
            <Input
              id={slugId}
              value={values.slug}
              // The app keys its state on the slug, so changing one after the
              // fact would read as a delete plus a create to every client that
              // already has it. New tips only.
              disabled={!canMutate || !isNew}
              onChange={(event) => {
                onChange({ ...values, slug: event.target.value });
              }}
              placeholder="swap-meal"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={localeId} className="text-xs font-medium text-muted-foreground">
              Locale
            </Label>
            <Input
              id={localeId}
              value={values.locale}
              disabled={!canMutate || !isNew}
              onChange={(event) => {
                onChange({ ...values, locale: event.target.value });
              }}
              placeholder="en-IN"
              className="w-28"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={iconId} className="text-xs font-medium text-muted-foreground">
              Icon
            </Label>
            <Select
              value={values.iconKey}
              disabled={!canMutate}
              onValueChange={(next) => {
                onChange({ ...values, iconKey: next as WaitTipIconName });
              }}
            >
              <SelectTrigger id={iconId} aria-label="Icon">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WAIT_TIP_ICONS.map((icon) => (
                  <SelectItem key={icon} value={icon}>
                    <span className="flex items-center gap-2">
                      <WaitTipIcon iconKey={icon} className="size-4" />
                      {ICON_LABELS[icon]}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={weightId} className="text-xs font-medium text-muted-foreground">
              Weight
            </Label>
            <Input
              id={weightId}
              type="number"
              min={1}
              max={100}
              value={values.weight}
              disabled={!canMutate}
              onChange={(event) => {
                onChange({ ...values, weight: Number(event.target.value) });
              }}
              className="w-24"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={activeId} className="text-xs font-medium text-muted-foreground">
              Live
            </Label>
            <div className="flex h-9 items-center">
              <Switch
                id={activeId}
                checked={values.isActive}
                disabled={!canMutate || isRetireBusy}
                onCheckedChange={(isActive) => {
                  onChange({ ...values, isActive });
                  // On an existing tip this is the retire switch, and it writes
                  // immediately: an operator flicking it expects it retired,
                  // not staged behind a Save they may never press.
                  onSetActive?.(isActive);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <Label htmlFor={textId} className="text-xs font-medium text-muted-foreground">
              Copy
            </Label>
            <span
              className={cn(
                'text-xs',
                values.text.length > WAIT_TIP_TEXT_MAX ? 'text-err-fg' : 'text-muted-foreground',
              )}
            >
              {values.text.length}/{WAIT_TIP_TEXT_MAX}
            </span>
          </div>
          <Textarea
            id={textId}
            value={values.text}
            disabled={!canMutate}
            rows={3}
            onChange={(event) => {
              onChange({ ...values, text: event.target.value });
            }}
            placeholder="Not in the mood for a meal? Swap it for something else."
          />
          <p className="text-xs text-muted-foreground">
            A tip cannot say the plan is ready, done, finished or complete — only the plan’s own status says
            that.
          </p>
        </div>

        {/*
          The preview is the point of the character limit. 320px is the
          narrowest phone the app supports, so copy that wraps to a fourth line
          here is copy that pushes the layout on a real device.
        */}
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-muted-foreground">Preview at phone width</span>
          <div className="max-w-[320px] rounded-xl border border-border bg-muted p-4">
            <div className="flex items-start gap-3">
              <WaitTipIcon
                iconKey={values.iconKey}
                className="mt-0.5 size-5 shrink-0 text-muted-foreground"
              />
              <p className="text-[15px] leading-relaxed text-foreground">
                {values.text.trim() === '' ? 'Your tip appears here.' : values.text}
              </p>
            </div>
          </div>
        </div>

        <fieldset className="flex flex-col gap-3 rounded-md border border-border px-4 py-3">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            Audience — leave everything unticked for everyone
          </legend>
          <div className="flex flex-wrap gap-6">
            {AUDIENCE_FACETS.map((facet) => (
              <div key={facet.key} className="flex flex-col gap-2">
                <span className="text-xs font-medium text-muted-foreground">{facet.label}</span>
                {facet.values.map((value) => {
                  const selected: readonly string[] = values.audience[facet.key];
                  const checked = selected.includes(value);
                  return (
                    <label key={value} className="flex items-center gap-2 text-[13px] text-foreground">
                      <Checkbox
                        checked={checked}
                        disabled={!canMutate}
                        onCheckedChange={(next) => {
                          const kept = selected.filter((entry) => entry !== value);
                          onChange({
                            ...values,
                            audience: {
                              ...values.audience,
                              [facet.key]: next === true ? [...kept, value] : kept,
                            },
                          });
                        }}
                      />
                      {value.replace(/_/g, ' ')}
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </fieldset>
      </div>
    </Drawer>
  );
}
